import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Car, 
  Settings, 
  Router,
  Cpu
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

const QuickAccessButton = ({ icon: Icon, label, onClick, loading }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="w-14 h-14 rounded-full" />
        <Skeleton className="w-20 h-4" />
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center gap-2">
      <Button
        onClick={onClick}
        size="icon"
        className="w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white transition-transform active:scale-95"
      >
        <Icon className="w-6 h-6" />
      </Button>
      <div className="text-sm text-center text-gray-700">
        {label}
      </div>
    </div>
  );
};

export default function QuickAccessWidget({ loading = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const handleNavigation = (path) => {
    navigate(path);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
      className="h-full"
    >
      <Card className="h-full rounded-xl border border-gray-200 overflow-visible relative">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            {loading ? <Skeleton className="w-32 h-6" /> : t('dashboard.quickAccess.title')}
          </h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <QuickAccessButton 
              icon={Car} 
              label={t('dashboard.quickAccess.fleet')} 
              onClick={() => handleNavigation('/dashboard/fleet/devices')}
              loading={loading}
            />
            <QuickAccessButton 
              icon={Cpu} 
              label={t('dashboard.quickAccess.edgeDevices')} 
              onClick={() => handleNavigation('/dashboard/edge-devices')}
              loading={loading}
            />
            <QuickAccessButton 
              icon={Router} 
              label={t('dashboard.quickAccess.iotGateways')} 
              onClick={() => handleNavigation('/dashboard/iot-gateways')}
              loading={loading}
            />
            <QuickAccessButton 
              icon={Settings} 
              label={t('dashboard.quickAccess.settings')} 
              onClick={() => handleNavigation('/dashboard/settings')}
              loading={loading}
            />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
} 